import {
  cloneElement,
  FC,
  MouseEvent,
  ReactElement,
  ReactNode,
  useCallback,
  useState,
} from "react";

import { Text, Box, BoxProps, SystemStyleObject } from "@hightouch/htui";

import { OrderBy } from "src/graphql";

import { TableColumn, TableColumnVariant } from "./table";
import { SortIcon } from "./sort-icon";
import {
  SkeletonItem,
  Skeleton,
  useSkeletonContext,
} from "src/components/skeletons";
import { getRowAnimationDelay, SKIP_HIGHLIGHT_ATTRIBUTE } from "./utils";

export type HeaderCellProps = {
  children: ReactNode;
  onClick?: (event: MouseEvent) => void;
  sortDirection?: OrderBy | null;
  top?: number | string;
  isSortMenu?: boolean;
  isDense?: boolean;
  sx?: SystemStyleObject;
  alignFirstHeader?: boolean;
  backgroundColor?: string;
  height?: string;
  divider?: boolean;
  variant?: TableColumnVariant;
  isLoading?: boolean;
};

export type TableCellProps<Row extends Record<string, any>> = {
  column: TableColumn<Row>;
  row: Row;
  onClick?: (row: Row, event: MouseEvent) => void;
  height?: string;
  isDense?: boolean;
  selected: boolean;
  rowIndex: number;
  isLastRow?: boolean;
  measureRef?: (node: Element | null) => void;
  virtualIndex?: number;
};

export const Cell: FC<
  Readonly<
    {
      children?: ReactNode;
      onClick?: (event: MouseEvent) => void;
      variant?: TableColumnVariant;
      measureRef?: (node: Element | null) => void;
    } & BoxProps
  >
> = ({ children, variant, measureRef, ...props }) => {
  return (
    <Box
      as="td"
      ref={measureRef}
      display="flex"
      alignItems="center"
      justifyContent={variant === "numeric" ? "flex-end" : "flex-start"}
      px={4}
      borderBottomWidth="hairline"
      borderBottomStyle="solid"
      {...props}
      borderColor={props.borderColor ?? "base.divider"}
    >
      {typeof children === "string" ? (
        <Text isTruncated variant={variant}>
          {children}
        </Text>
      ) : (
        children
      )}
    </Box>
  );
};

export function TableCell<RowData extends Record<string, any>>({
  column: {
    name,
    cell,
    cellSx = {},
    divider,
    disabled,
    whitespace,
    variant,
    min,
    max,
    skeleton: CustomSkeleton,
    skipHighlight,
    onCellMouseEnter,
    onCellMouseLeave,
  },
  row,
  height,
  isDense,
  rowIndex,
  selected,
  isLastRow = false,
  measureRef,
  virtualIndex,
}: Readonly<TableCellProps<RowData>>): ReactElement {
  const { isLoading } = useSkeletonContext();

  const handleClick = useCallback(
    (event: MouseEvent) => {
      if (disabled) {
        event.stopPropagation();
      }
    },
    [disabled],
  );

  const heightIsSet = height && height !== "auto" && height !== "max-content";

  const animationDelay = getRowAnimationDelay(rowIndex);

  const computedCellSx = typeof cellSx === "function" ? cellSx(row) : cellSx;

  return (
    <Cell
      onClick={handleClick}
      onMouseEnter={onCellMouseEnter ? () => onCellMouseEnter(row) : undefined}
      onMouseLeave={onCellMouseLeave ? () => onCellMouseLeave(row) : undefined}
      // measureRef and data-index must sit on the same element; measureElement pairs them.
      measureRef={measureRef}
      data-index={virtualIndex}
      {...(skipHighlight?.(row) ? { [SKIP_HIGHLIGHT_ATTRIBUTE]: "" } : {})}
      minHeight={height}
      whiteSpace={whitespace || "nowrap"}
      p={isDense ? 2 : heightIsSet ? undefined : 4}
      borderLeftWidth={isDense || divider ? "hairline" : undefined}
      borderLeftStyle={isDense || divider ? "solid" : undefined}
      variant={variant}
      sx={{
        "&:last-of-type": {
          borderRightWidth: isDense ? "hairline" : undefined,
          borderRightStyle: isDense ? "solid" : undefined,
          borderRightColor: "base.divider",
          pr: 6,
        },
        ...(isLastRow && {
          borderBottom: "none",
        }),
        ...computedCellSx,
      }}
    >
      {isLoading ? (
        CustomSkeleton ? (
          cloneElement(CustomSkeleton, { animationDelay })
        ) : (
          <SkeletonItem
            id={`${name}-${rowIndex}`}
            height={height ?? "30px"}
            width={!min || !max ? "100px" : "100%"}
            borderRadius="md"
            // Note: the first row of the table starts with a delay,
            // but the header row does not.
            animationDelay={animationDelay}
          />
        )
      ) : (
        cell(row, rowIndex, selected)
      )}
    </Cell>
  );
}

export const HeaderCell: FC<HeaderCellProps> = ({
  children,
  sortDirection,
  top,
  isSortMenu,
  isDense,
  variant,
  sx = {},
  alignFirstHeader = false,
  backgroundColor = "white",
  divider,
  isLoading,
  onClick,
  ...props
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const isSticky = typeof top !== "undefined";
  const isClickable = Boolean(onClick);
  return (
    <Box
      as="th"
      cursor={onClick ? "pointer" : "auto"}
      alignItems="center"
      display="flex"
      flexWrap="nowrap"
      whiteSpace="nowrap"
      py={isDense ? 2 : undefined}
      px={isDense ? "8px !important" : isSortMenu ? 0 : 4}
      pos={isSticky ? "sticky" : undefined}
      zIndex={isSticky ? 1 : undefined}
      top={top}
      bg={backgroundColor}
      color="text.primary"
      borderLeftWidth={isDense || divider ? "hairline" : undefined}
      borderLeftStyle={isDense || divider ? "solid" : undefined}
      borderTopWidth={isDense ? "hairline" : undefined}
      borderTopStyle={isDense ? "solid" : undefined}
      borderBottomWidth="hairline"
      borderBottomStyle="solid"
      fontWeight="semibold"
      fontSize={isDense ? "sm" : "md"}
      borderColor="base.divider !important"
      justifyContent={variant === "numeric" ? "flex-end" : "flex-start"}
      _last={{
        pr: 6,
        borderRightWidth: isDense ? "hairline" : undefined,
        borderRightStyle: isDense ? "solid" : undefined,
        borderRightColor: "base.divider",
      }}
      sx={{
        ...(!alignFirstHeader && {
          "&:first-of-type": {
            pl: 6,
          },
        }),

        ...sx,
      }}
      {...props}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Skeleton isLoading={Boolean(isLoading)} borderRadius="md">
        {children}
        {isClickable && (
          <SortIcon isHovered={isHovered} sortDirection={sortDirection} />
        )}
      </Skeleton>
    </Box>
  );
};
